<template>
  <div class="column-picker">
    <div class="column-picker__list">
      <div class="column-picker__list-head">
        <FormGroup :label="labelOut || 'Hidden columns'" class="tw-flex-1 mb-0"/>
        <span class="column-picker__badge">{{ hiddenColumns.length }}</span>
      </div>
      <DsInput style="margin-bottom: 0.75rem;" icon="search" placeholder="Search field..." is-simplified v-model="queryHidden" @update:modelValue="filterHiddenColumns"/>
      <draggable class="draggable-list" :list="hiddenColumns" @change="apply" item-key="id" group="columns" ghost-class="draggable-list-item--ghost">
        <template #item="{ element }">
          <div class="draggable-list-item" @click="toggleColumn(element, 'visible')">
            <Icon name="move" size="14" class="draggable-list-item__handle"/>
            <span class="draggable-list-item__label">{{ element.humanizedName }}</span>
            <Icon name="arrow-right" size="14" class="draggable-list-item__action"/>
          </div>
        </template>
        <template #footer v-if="hiddenColumns.length === 0">
          <p class="column-picker__empty">{{ queryHidden ? 'No matching columns' : 'All columns are selected' }}</p>
        </template>
      </draggable>
      <button v-if="hiddenColumnsCopy.length" type="button" class="column-picker__bulk-action" @click="selectAll">
        Add all
      </button>
    </div>

    <div class="column-picker__center-container">
      <Icon name="expand"/>
    </div>

    <div class="column-picker__list">
      <div class="column-picker__list-head">
        <form-group :label="labelIn || 'Visible columns'" class="tw-flex-1 mb-0"/>
        <span class="column-picker__badge column-picker__badge--active">{{ visibleColumns.length }}</span>
      </div>
      <DsInput style="margin-bottom: 0.75rem;" icon="search" placeholder="Search field..." is-simplified v-model="queryVisible" @update:modelValue="filterVisibleColumns"/>
      <draggable class="draggable-list" :list="visibleColumns" @change="apply" item-key="id" group="columns" ghost-class="draggable-list-item--ghost">
        <template #item="{ element }">
          <div class="draggable-list-item draggable-list-item--selected" @click="toggleColumn(element, 'hidden')">
            <Icon name="move" size="14" class="draggable-list-item__handle"/>
            <span class="draggable-list-item__label">{{ element.humanizedName }}</span>
            <Icon name="remove" size="14" class="draggable-list-item__action draggable-list-item__action--remove"/>
          </div>
        </template>
        <template #footer v-if="visibleColumns.length === 0">
          <p class="column-picker__empty">Nothing selected yet</p>
        </template>
      </draggable>
      <button v-if="visibleColumnsCopy.length" type="button" class="column-picker__bulk-action column-picker__bulk-action--danger" @click="clearAll">
        Remove all
      </button>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue';
  import draggable from 'vuedraggable';
  import _difference from 'lodash/difference.js';
  import { humanize } from '../../constants/properties.js';
  import FormGroup from '@/components/Form/FormGroup.vue'
  import Icon from '@/components/Icon/Icon.vue'
  import { _uniqueBy, getReportFieldById } from '@/util/helpers'
  import { viewListOptions } from '@/constants/config'

  export default defineComponent({
    name: 'DraggableColumnPicker',
    components: { Icon, FormGroup, draggable },
    props: {
      labelIn: String,
      labelOut: String,
      options: Array,
      modelValue: Array,
    },
    emits: ['update:modelValue'],
    data() {
      return {
        hiddenColumns: [],
        visibleColumns: [],
        hiddenColumnsCopy: [],
        visibleColumnsCopy: [],
        queryHidden: '',
        queryVisible: '',
      };
    },
    computed: {
      leftovers() {
        var leftovers = _difference(this.options || viewListOptions, this.modelValue).sort()

        if (!this.isInFundingDashboard) {
          var fundingDashboardColumns = ['total_investment', 'funding_date', 'funding_type', 'funding_amount']

          leftovers = leftovers.filter(column => {
            return !fundingDashboardColumns.includes(column)
          })
        }

        leftovers = leftovers.map(column => {
          return { id: column, name: column, humanizedName: this.humanizeColumn(column) }
        })

        return _uniqueBy(leftovers, 'id')
      },
      isInFundingDashboard() {
        return this.$route.path === '/dashboards/funding-list'
      },
    },
    methods: {
      humanizeColumn(column) {
        if (column.indexOf('report_field_') >= 0) {
          var reportFieldId = column.replace('report_field_', '')
          var reportField = getReportFieldById(reportFieldId)

          if (!reportField) {
            return 'report'
          }

          return reportField.label
        }

        return humanize(column)
      },
      filterHiddenColumns(value) {
        if (!value) {
          this.hiddenColumns = this.hiddenColumnsCopy;
          return;
        }

        this.hiddenColumns = this.hiddenColumnsCopy.filter(item => item.humanizedName.toLowerCase().includes(value.toLowerCase()));
      },
      filterVisibleColumns(value) {
        if (!value) {
          this.visibleColumns = this.visibleColumnsCopy;
          return;
        }

        this.visibleColumns = this.visibleColumnsCopy.filter(item => item.humanizedName.toLowerCase().includes(value.toLowerCase()));
      },
      apply() {
        this.$emit('update:modelValue', this.visibleColumns.map(item => item.id));
      },
      toggleColumn(column, targetList) {
        if (targetList === 'visible') {
          // Verwijder het veld uit de kopie (back-up) van hiddenColumns
          this.hiddenColumnsCopy = this.hiddenColumnsCopy.filter(item => item.id !== column.id);
          this.resetHiddenColumns();
          // Voeg toe aan visibleColumns
          this.visibleColumns.push(column);
          this.visibleColumnsCopy = [...this.visibleColumns]; // sync de kopie

          this.queryHidden = ''; // reset de zoekbalk
        } else {
          this.visibleColumnsCopy = this.visibleColumnsCopy.filter(item => item.id !== column.id);
          this.resetVisibleColumns();
          this.hiddenColumns.push(column);
          this.hiddenColumnsCopy = [...this.hiddenColumns];

          this.queryVisible = '';
        }
        setTimeout(() => {
          this.apply();
        }, 200);
      },
      selectAll() {
        this.visibleColumns = [...this.visibleColumnsCopy, ...this.hiddenColumnsCopy];
        this.visibleColumnsCopy = [...this.visibleColumns];
        this.hiddenColumns = [];
        this.hiddenColumnsCopy = [];
        this.queryHidden = '';
        this.apply();
      },
      clearAll() {
        this.hiddenColumns = [...this.hiddenColumnsCopy, ...this.visibleColumnsCopy];
        this.hiddenColumnsCopy = [...this.hiddenColumns];
        this.visibleColumns = [];
        this.visibleColumnsCopy = [];
        this.queryVisible = '';
        this.apply();
      },
      resetHiddenColumns() {
        this.hiddenColumns = [...this.hiddenColumnsCopy];
      },
      resetVisibleColumns() {
        this.visibleColumns = [...this.visibleColumnsCopy];
      },
      mapColumn(column) {
        if (column === 'elastic_id') return { id: 'elastic_id', humanizedName: 'ID' };
        if (column === 'stage') return { id: 'stage', humanizedName: 'Growth Stage' };
        if (column == 'industry_codes') return { id: column, name: column, humanizedName: 'Main NACE Code' }
        if (column == 'nace_codes') return { id: column, name: column, humanizedName: 'Main NACE Code' }
        if (column == 'claimees') return { id: column, name: column, humanizedName: 'Claimees (Contributors of the actor)' }
        if (column == 'contact_email_list') return { id: column, name: column, humanizedName: 'Founder & Employees (can be non-contributors)' }

        return { id: column, humanizedName: humanize(column) };
      },
      sortByHumanizedName(a, b) {
        return a.humanizedName.localeCompare(b.humanizedName);
      },
    },
    created() {
      this.hiddenColumns = this.leftovers;
      this.hiddenColumnsCopy = this.hiddenColumns;
      this.visibleColumns = (this.modelValue || []).map(this.mapColumn);
      this.visibleColumnsCopy = this.visibleColumns;
    },
  });
</script>

<style lang="scss" scoped>
  .column-picker {
    display: flex;
    gap: 20px;
    align-items: start;
    max-height: 400px;
  }

  .column-picker__list {
    flex: 1;
    min-width: 0;
  }

  .column-picker__list-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 4px;
  }

  .column-picker__badge {
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
    background: #f3f4f6;
    border-radius: 999px;
    padding: 2px 9px;
    line-height: 1.4;
  }

  .column-picker__badge--active {
    color: var(--primary, #2563eb);
    background: rgba(37, 99, 235, 0.1);
  }

  .column-picker__bulk-action {
    display: block;
    width: 100%;
    margin-top: 8px;
    padding: 6px 10px;
    background: none;
    border: 1px dashed #d1d5db;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .column-picker__bulk-action:hover {
    border-color: var(--primary, #2563eb);
    color: var(--primary, #2563eb);
    background: rgba(37, 99, 235, 0.05);
  }

  .column-picker__bulk-action--danger:hover {
    border-color: #dc2626;
    color: #dc2626;
    background: rgba(220, 38, 38, 0.05);
  }

  .column-picker__empty {
    margin: 0;
    padding: 24px 8px;
    text-align: center;
    font-size: 12px;
    color: #9ca3af;
  }

  .draggable-list {
    border: 1px solid #eceef1;
    border-radius: 12px;
    padding: 6px;
    min-height: 220px;
    background: #fafbfc;
    max-height: 320px;
    overflow: scroll;
  }

  .draggable-list-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    margin: 3px 0;
    background: white;
    border-radius: 8px;
    font-size: 13px;
    color: #374151;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;
    border: 1px solid #eceef1;
  }

  .draggable-list-item:hover {
    background: #f5f8ff;
    border-color: var(--primary, #2563eb);
  }

  .draggable-list-item--ghost {
    opacity: 0.5;
  }

  .draggable-list-item--selected {
    background: #f8fafc;
  }

  .draggable-list-item--selected:hover {
    background: #fef2f2;
    border-color: #dc2626;
  }

  .draggable-list-item__handle {
    flex-shrink: 0;
    color: #c1c7d0;
    cursor: grab;
  }

  .draggable-list-item__label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .draggable-list-item__action {
    flex-shrink: 0;
    color: #9ca3af;
    opacity: 0;
    transition: opacity 0.15s ease;
  }

  .draggable-list-item:hover .draggable-list-item__action {
    opacity: 1;
  }

  .draggable-list-item__action--remove:hover {
    color: #dc2626;
  }

  .column-picker__center-container {
    align-content: center;
    height: 300px;

    :deep(path) {
      fill: var(--primary) !important;
    }

  }
</style>
